import { ArrowDown, Loader2, RotateCcw } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useSelector, useStore } from "react-redux";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { agentClient } from "../agentClient";
import { ThreadPrimitive, useAui, useAuiState } from "../aui";
import { ContextChoices } from "../choices/ContextChoices";
import { CARD_SCROLLER_PROPS } from "../choices/followCard";
import type { AgentState, OutgoingFile, ThreadState } from "../store/agentSlice";
import { AssistantMessage } from "./AssistantMessage";
import { EventMessage } from "./EventMessage";
import { LIMIT_REACHED } from "./models";
import { useIsPhone } from "./PhoneSheet";
import { focusComposer, UserMessage } from "./UserMessage";
import { useKeepAtBottom } from "./useKeepAtBottom";
import { useThreadId } from "./useThreadId";

/**
 * The thread's scroll area. It keeps a minimum height (the composer and a line of the thread) when
 * the approvals strip above it grows, so a tall card never pushes the composer out of the lip.
 */
export const THREAD_VIEWPORT_CLASS =
  "flex min-h-40 flex-1 flex-col overflow-y-auto px-4 max-sm:px-3";

/** Older messages load when the top of the thread scrolls into view. */
function LoadEarlier() {
  const aui = useAui();
  const hasEarlier = useAuiState((state) => state.thread.hasEarlier);
  const loading = useAuiState((state) => state.thread.isLoadingEarlier);
  const sentinel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const element = sentinel.current;
    if (!hasEarlier || !element) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !aui.thread.getState().isLoadingEarlier) {
        void aui.thread.loadEarlier();
      }
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [aui, hasEarlier]);
  if (!hasEarlier) return null;
  return (
    <div ref={sentinel} className="flex h-8 shrink-0 items-center justify-center">
      {loading && <Loader2 className="h-4 w-4 text-muted-foreground motion-safe:animate-spin" />}
    </div>
  );
}

/**
 * The user's last message, to ask again: its text, and its files with the pages sent of them. None
 * when the failed turn was not the user's own: an event or a decided card since that message (the
 * run did things a retry would do again), or messages queued (they run on their own), or for a
 * voice note (only its channel sends one).
 */
function lastQuestion(thread: ThreadState, runId: number) {
  if (thread.pendingSeqs.length > 0) return null;
  const index = thread.messages.findLastIndex((candidate) => candidate.role === "user");
  if (index < 0) return null;
  if (thread.messages.slice(index + 1).some((later) => later.role === "event")) return null;
  const decided = Object.values(thread.choices).some(
    (choice) => choice.run_id === runId && choice.status === "decided"
  );
  if (decided) return null;
  // A voice note can't be sent from here: asking again would lose it.
  if (thread.messages[index].parts.some((part) => part.kind === "voice")) return null;
  const texts: string[] = [];
  const files: OutgoingFile[] = [];
  const pages: Record<number, string> = {};
  for (const part of thread.messages[index].parts) {
    if (part.kind === "text") texts.push(part.text);
    if (part.kind !== "file" || part.thread_file_id == null) continue;
    files.push({ id: part.thread_file_id, name: part.name, contentType: part.media_type });
    if (part.pages) pages[part.thread_file_id] = part.pages;
  }
  return { text: texts.join("\n\n"), files, pages };
}

/**
 * Why the last answer stopped, when it failed (a budget, an outage), with Try again: the user's
 * last message asked again. A stop says nothing here (the answer it cut says Stopped). Try again
 * is left out where it would lose or repeat something: a draft in the composer (sending clears
 * it), a message of this thread still sending or failed (or this run's already asked again), and
 * the daily limit (asking again fails the same way).
 */
function RunError() {
  const threadId = useThreadId();
  // The run only: the thread object changes on every streamed delta.
  const run = useSelector((state: { agent: AgentState }) =>
    threadId == null ? undefined : state.agent.threads[threadId]?.run
  );
  // Whether there is a question to ask again, not the question: a streaming answer re-renders
  // nothing here. Pressing reads it from the store.
  const askable = useSelector((state: { agent: AgentState }) => {
    const thread = threadId == null ? undefined : state.agent.threads[threadId];
    return thread?.run?.status === "failed" && lastQuestion(thread, thread.run.id) !== null;
  });
  const store = useStore<{ agent: AgentState }>();
  const outgoing = useSelector((state: { agent: AgentState }) =>
    Object.values(state.agent.outbox).some(
      (message) => message.threadId === threadId && message.state !== "sent"
    )
  );
  const draft = !useAuiState((state) => state.composer.isEmpty);
  const [asked, setAsked] = useState<number | null>(null);
  const phone = useIsPhone();
  if (run?.status !== "failed") return null;
  const error = run.error || "The run failed.";
  const retry = askable && !draft && !outgoing && asked !== run.id && error !== LIMIT_REACHED;
  return (
    <div role="alert" className="flex items-center gap-0.5 text-xs text-destructive">
      <span className="mr-1 min-w-0 break-words">{error}</span>
      {threadId != null && retry && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-6 w-6 shrink-0 max-sm:h-10 max-sm:w-10"
          aria-label="Try again"
          onClick={(event) => {
            const thread = store.getState().agent.threads[threadId];
            const question = thread && lastQuestion(thread, run.id);
            if (!question) return;
            // On a phone the keyboard would cover the answer coming in.
            if (!phone) focusComposer(event.currentTarget);
            setAsked(run.id);
            agentClient.send(threadId, question.text, question.files, question.pages);
          }}
        >
          <RotateCcw className="!size-3.5" />
        </Button>
      )}
    </div>
  );
}

/**
 * The conversation of the AgentRuntime around it, newest at the bottom, with `children` (the
 * composer) pinned below. It keeps to the bottom while answers stream, unless the user scrolled up.
 */
export function AgentThread({ children, className }: { children?: ReactNode; className?: string }) {
  const viewport = useRef<HTMLDivElement>(null);
  const content = useRef<HTMLDivElement>(null);
  useKeepAtBottom(viewport, content);
  return (
    <ThreadPrimitive.Root className={cn("flex min-h-0 flex-1 flex-col bg-card", className)}>
      <ContextChoices />
      <ThreadPrimitive.Viewport
        ref={viewport}
        autoScroll
        scrollToBottomOnInitialize
        scrollToBottomOnThreadSwitch={false}
        {...CARD_SCROLLER_PROPS}
        className={THREAD_VIEWPORT_CLASS}
      >
        <LoadEarlier />
        <div ref={content} className="mx-auto flex w-full max-w-thread flex-col gap-5 pb-4 pt-3">
          <ThreadPrimitive.Messages
            components={{ UserMessage, AssistantMessage, SystemMessage: EventMessage }}
          />
          <RunError />
        </div>
        <ThreadPrimitive.ViewportFooter className="sticky bottom-0 mt-auto bg-card pb-3 max-sm:pb-0">
          {/* Floats over the thread, 8px or more clear of the Files card and composer below. */}
          <ThreadPrimitive.ScrollToBottom asChild>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="absolute -top-11 left-1/2 h-8 w-8 -translate-x-1/2 rounded-full shadow-sm disabled:invisible max-sm:h-9 max-sm:w-9"
              aria-label="Latest"
            >
              <ArrowDown />
            </Button>
          </ThreadPrimitive.ScrollToBottom>
          {children}
        </ThreadPrimitive.ViewportFooter>
      </ThreadPrimitive.Viewport>
    </ThreadPrimitive.Root>
  );
}
